import {
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Truck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import ProveedorModal from '../components/proveedores/ProveedorModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { formatearRut, limpiarRut } from '../utils/rut'

function formatearFecha(valor) {
  if (!valor) return '—'

  const fecha = new Date(valor)

  if (Number.isNaN(fecha.getTime())) return '—'

  return fecha.toLocaleDateString('es-CL')
}

function Proveedores() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const [proveedores, setProveedores] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [proveedorEditar, setProveedorEditar] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setProveedores([])
      setCargando(false)
      return
    }

    cargarProveedores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarProveedores = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('proveedores')
        .select(`
          id,
          empresa_id,
          rut,
          razon_social,
          activo,
          creado_en,
          actualizado_en
        `)
        .eq('empresa_id', empresaActual.id)
        .order('razon_social', { ascending: true })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setProveedores(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar proveedores:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los proveedores.'
      )
      setProveedores([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const proveedoresFiltrados = proveedores.filter((proveedor) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const nombreCoincide = (proveedor.razon_social || '')
      .toLowerCase()
      .includes(termino)

    const rutCoincide = limpiarRut(proveedor.rut).includes(
      limpiarRut(busqueda)
    )

    return nombreCoincide || rutCoincide
  })

  const abrirCreacion = () => {
    setProveedorEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (proveedor) => {
    setProveedorEditar(proveedor)
    setModalAbierto(true)
  }

  const cerrarModalProveedor = () => {
    setModalAbierto(false)
    setProveedorEditar(null)
  }

  const alGuardar = (proveedorGuardado) => {
    const fueEdicion = Boolean(proveedorEditar)

    cerrarModalProveedor()
    cargarProveedores()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion
        ? 'Proveedor actualizado'
        : 'Proveedor creado',
      mensaje: `El proveedor "${proveedorGuardado?.razon_social}" fue ${
        fueEdicion ? 'actualizado' : 'creado'
      } correctamente.`,
    })
  }

  const aplicarCambioEstado = async (
    proveedor,
    activar,
    esDesactivacion
  ) => {
    if (esDesactivacion) {
      actualizarModal({
        procesando: true,
        mostrarCancelar: false,
        textoAceptar: 'Desactivando...',
      })
    }

    try {
      setActualizandoId(proveedor.id)

      const { error: errorActualizar } = await supabase
        .from('proveedores')
        .update({ activo: activar })
        .eq('id', proveedor.id)
        .eq('empresa_id', empresaActual.id)

      if (errorActualizar) {
        throw errorActualizar
      }

      if (!montadoRef.current) return

      await cargarProveedores()

      if (esDesactivacion) {
        abrirModal({
          tipo: 'success',
          titulo: 'Proveedor desactivado',
          mensaje: 'El proveedor fue desactivado correctamente.',
        })
      }
    } catch (errorEstado) {
      console.error(
        'Error al actualizar estado del proveedor:',
        errorEstado
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorEstado.message ||
          'No fue posible actualizar el estado del proveedor.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const cambiarEstado = (proveedor) => {
    const activar = !proveedor.activo

    if (!activar) {
      abrirModal({
        tipo: 'confirm',
        titulo: 'Desactivar proveedor',
        mensaje: `¿Deseas desactivar a "${proveedor.razon_social}"?`,
        textoAceptar: 'Desactivar',
        textoCancelar: 'Cancelar',
        mostrarCancelar: true,
        onAceptar: () =>
          aplicarCambioEstado(proveedor, false, true),
      })
      return
    }

    aplicarCambioEstado(proveedor, true, false)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando proveedores...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarProveedores}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (proveedores.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Truck
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay proveedores registrados
          </h3>

          <p className="mt-2 text-slate-500">
            Presiona “Nuevo proveedor” para ingresar el primer
            registro.
          </p>

          <button
            type="button"
            onClick={abrirCreacion}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
          >
            <Plus size={19} />
            Ingresar primer proveedor
          </button>
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre o RUT"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {proveedoresFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron proveedores para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm sm:min-w-[640px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="px-6 py-3">RUT</th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Fecha de creación
                  </th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {proveedoresFiltrados.map((proveedor) => (
                  <tr key={proveedor.id}>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {proveedor.razon_social}
                    </td>

                    <td className="px-6 py-4 text-slate-600">
                      {proveedor.rut
                        ? formatearRut(proveedor.rut)
                        : '—'}
                    </td>

                    <td className="px-6 py-4">
                      {proveedor.activo ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          Inactivo
                        </span>
                      )}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                      {formatearFecha(proveedor.creado_en)}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => abrirEdicion(proveedor)}
                          title="Editar"
                          aria-label="Editar proveedor"
                          className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={() => cambiarEstado(proveedor)}
                          disabled={
                            actualizandoId === proveedor.id
                          }
                          title={
                            proveedor.activo
                              ? 'Desactivar'
                              : 'Reactivar'
                          }
                          aria-label={
                            proveedor.activo
                              ? 'Desactivar proveedor'
                              : 'Reactivar proveedor'
                          }
                          className={
                            proveedor.activo
                              ? 'rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                              : 'rounded-lg border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                          }
                        >
                          {actualizandoId === proveedor.id ? (
                            <LoaderCircle
                              size={16}
                              className="animate-spin"
                            />
                          ) : proveedor.activo ? (
                            <PowerOff size={16} />
                          ) : (
                            <Power size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Administración
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Proveedores
          </h2>

          <p className="mt-2 text-slate-500">
            Administra los proveedores de la empresa
            seleccionada.
          </p>
        </div>

        <button
          type="button"
          onClick={abrirCreacion}
          className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
        >
          <Plus size={20} />
          Nuevo proveedor
        </button>
      </div>

      {renderContenido()}

      {modalAbierto && (
        <ProveedorModal
          proveedor={proveedorEditar}
          onClose={cerrarModalProveedor}
          onGuardado={alGuardar}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Proveedores
